import { useEffect, useRef } from 'react';
import type { BagItem } from '../../lib/bag';
import { formatBRL } from '../../lib/format';
import { useDialogA11y } from '../../hooks/useDialogA11y';

// Sacola (drawer lateral): lista as linhas com stepper de quantidade, subtotal e
// o gatilho para o checkout. Fecha por Esc, clique fora e botão.

interface Props {
  bag: BagItem[];
  count: number;
  subtotal: number;
  onClose: () => void;
  onChangeQty: (key: string, delta: number) => void;
  onRemove: (key: string) => void;
  onCheckout: () => void;
  /** QA-28 B-06 — ids de produto que o catálogo ATUAL diz indisponíveis (esgotados ou fora do cardápio). A
   *  linha da sacola guarda o snapshot do produto de quando foi adicionado, então sem esta lista o cliente só
   *  descobria o problema dentro do checkout, depois de avançar. Ausente = nada a sinalizar. */
  indisponiveisIds?: Set<string>;
}

function descreveConfig(i: BagItem): string {
  return [...(i.pizzaResumo ?? []), i.variacao?.nome, ...i.adicionais.map((a) => a.nome)].filter(Boolean).join(' · ');
}

export function BagDrawer({
  bag,
  count,
  subtotal,
  onClose,
  onChangeQty,
  onRemove,
  onCheckout,
  indisponiveisIds,
}: Props) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  // A linha já dizia "remova para finalizar", mas o botão seguia ativo e a mesa enviava assim mesmo (QA 31/10/2026).
  const temIndisponivel = bag.some(
    (i) => i.produto.esgotado || (indisponiveisIds?.has(i.produto.produtoId) ?? false)
  );
  const dialogRef = useRef<HTMLElement | null>(null);
  // QA-28 M-07 — Tab preso ao drawer + catálogo de fundo `aria-hidden` + foco de volta no botão da sacola.
  useDialogA11y(dialogRef);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="loja-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <aside ref={dialogRef} className="loja-drawer" role="dialog" aria-modal="true" aria-label="Sua sacola">
        <div className="loja-drawer-head">
          <h3>
            Sua sacola {count > 0 && <span className="loja-drawer-count">{count}</span>}
          </h3>
          <button ref={closeRef} className="loja-close" onClick={onClose} aria-label="Fechar sacola">
            ×
          </button>
        </div>

        {bag.length === 0 ? (
          <div className="loja-drawer-empty">
            <div className="loja-empty-emoji" aria-hidden="true">
              🧺
            </div>
            <h3>Sua sacola está vazia</h3>
            <p>Escolha algo delicioso do cardápio para começar.</p>
            <button className="loja-btn loja-btn--primary" onClick={onClose}>
              Voltar ao cardápio
            </button>
          </div>
        ) : (
          <>
            <div className="loja-drawer-body">
              {bag.map((i) => {
                const cfg = descreveConfig(i);
                // QA-28 B-06 — indisponível = esgotado no snapshot OU no catálogo atual (inclui produto que
                // saiu do cardápio), para o cliente ver o problema JÁ na sacola.
                const indisponivel =
                  i.produto.esgotado || (indisponiveisIds?.has(i.produto.produtoId) ?? false);
                return (
                  <div className="loja-line" key={i.key}>
                    <div className="loja-line-info">
                      <b>{i.produto.nome}</b>
                      {cfg && <small>{cfg}</small>}
                      {/* QA-28 B-06 — item que ESGOTOU só aparecia no checkout, depois de o cliente avançar.
                          Aqui, com texto + ícone (nunca só cor) e a ação de remover ao lado. */}
                      {indisponivel && (
                        <span className="loja-line-esgotado" role="status">
                          <span aria-hidden="true">⚠</span> Esgotado - remova para finalizar
                        </span>
                      )}
                      <span className="loja-line-unit">{formatBRL(i.unitPrice)} un.</span>
                    </div>
                    <div className="loja-line-side">
                      <div className="loja-qty" role="group" aria-label={`Quantidade de ${i.produto.nome}`}>
                        <button onClick={() => onChangeQty(i.key, -1)} aria-label="Diminuir">
                          −
                        </button>
                        {/* QA-28 B-06 — a quantidade mudava sem NENHUM anúncio (o QuickView já tinha
                            aria-live): quem usa leitor de tela clicava +/− sem retorno. */}
                        <span aria-live="polite">{i.qty}</span>
                        <button onClick={() => onChangeQty(i.key, 1)} aria-label="Aumentar">
                          +
                        </button>
                      </div>
                      <b className="loja-line-total">{formatBRL(i.unitPrice * i.qty)}</b>
                      <button
                        className="loja-line-rm"
                        onClick={() => onRemove(i.key)}
                        aria-label={`Remover ${i.produto.nome}`}
                      >
                        Remover
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="loja-drawer-foot">
              <div className="loja-sumline">
                <span>Subtotal</span>
                <b>{formatBRL(subtotal)}</b>
              </div>
              <button
                className="loja-btn loja-btn--primary loja-btn--block loja-btn--lg"
                onClick={onCheckout}
                disabled={temIndisponivel}
                aria-describedby={temIndisponivel ? 'loja-sacola-trava' : undefined}
              >
                Finalizar pedido
              </button>
              {temIndisponivel && (
                <p id="loja-sacola-trava" className="loja-drawer-trava">
                  Remova os itens esgotados para finalizar.
                </p>
              )}
              <button className="loja-btn loja-btn--text loja-btn--block" onClick={onClose}>
                Continuar escolhendo
              </button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
